/**
 * Lecture d'un `passeplat_wsid` : nom d'hôte réel, et sens de l'appel.
 *
 * POURQUOI DU CODE ET PAS DU YAML — le décodage d'un wsid n'est pas exprimable
 * en reactive-json : c'est une substitution ordonnée (`--` avant `-`), et le
 * sens de l'appel s'en déduit par comparaison à un hôte que le YAML ne connaît
 * pas. Aucun rjBuild, aucune capacité Drupal ne rend ça. Ce module ne CRÉE pas
 * de code, il en SUPPRIME : les trois processors WSolution portaient chacun leur
 * copie de `formatWsLabel`, toutes les trois fausses de la même façon.
 *
 * Un wsid s'écrit `<scheme>---<hôte encodé>---<uid>`, et l'encodage de l'hôte
 * n'est pas une simple substitution : un point devient `-`, un TIRET devient
 * `--` (WebServiceManager::getDestinationUrl(), côté PHP, décode dans cet
 * ordre). Décoder tous les tirets en points rendait `drupal.orbit..2.dev.emerya.eu`
 * pour `drupal.orbit-2.dev.emerya.eu` : lisible mais faux, et surtout
 * incomparable à un vrai nom d'hôte.
 *
 * Le sens de l'appel se DÉDUIT ensuite de l'hôte de destination : un webservice
 * qui vise cette instance est le passe-plat ENTRANT (les appels arrivent sur le
 * site), tous les autres sont sortants. Aucun réglage à tenir à jour, la règle
 * suit l'instance.
 *
 * LIMITE ASSUMÉE : « cette instance », c'est l'hôte Drupal et celui du front.
 * Une instance qui relaierait vers un TROISIÈME de ses propres noms (alias,
 * sous-domaine de site) verrait ce webservice compté comme sortant — sans
 * erreur, juste un écran qui dit « Sortant ». Le jour où ça arrive, la réponse
 * n'est pas d'allonger cette liste côté client mais de faire porter le sens par
 * le back, qui connaît les destinations déclarées.
 */

import { DRUPAL_BASE_URL } from '../config/drupal';

/**
 * `https---drupal-orbit--2-dev-emerya-eu---userid` → `drupal.orbit-2.dev.emerya.eu`.
 *
 * Rend la clé telle quelle si elle n'a pas la forme attendue : mieux vaut
 * afficher un wsid brut qu'un libellé inventé.
 */
export function wsidHost(wsid) {
    if (!wsid) return '';
    const parts = String(wsid).split('---');
    if (parts.length < 3) return String(wsid);

    // Découper d'abord sur `--` (les tirets littéraux) évite le sentinelle du
    // code PHP : ce qui reste dans chaque morceau ne contient que des points
    // encodés.
    return parts[1].split('--').map((chunk) => chunk.replace(/-/g, '.')).join('-');
}

/** Les hôtes qui SONT cette instance : le back Drupal et le front. */
function ownHosts() {
    const hosts = [];
    try {
        hosts.push(new URL(DRUPAL_BASE_URL).hostname);
    } catch {
        // URL de base illisible : on se rabat sur le seul hôte courant.
    }
    if (typeof window !== 'undefined' && window.location?.hostname) {
        hosts.push(window.location.hostname);
    }
    return hosts.filter(Boolean).map((h) => h.toLowerCase());
}

/**
 * Vrai quand le webservice est le passe-plat ENTRANT de cette instance.
 *
 * Égalité d'hôte, jamais inclusion : `drupal.exemple.fr.pirate.com` ne doit pas
 * pouvoir se faire passer pour le site dans les écrans.
 *
 * @param {string} wsid
 *   Le `passeplat_wsid` du webservice.
 */
export function isIncomingWsid(wsid) {
    const host = wsidHost(wsid).toLowerCase();
    return !!host && ownHosts().includes(host);
}

/** Paramètres d'URL qui désignent un webservice et son préfiltre éventuel. */
export const WSOLUTION_PARAMS = ['id', 'wsid', 'path', 'referer', 'addr'];

/**
 * Liens vers le tableau de bord et les logs d'un webservice, préfiltre compris.
 *
 * Les valeurs vides sont OMISES. C'est la raison d'être de cette fonction : un
 * gabarit PathLink laisse le jeton en clair quand sa valeur est vide
 * (`referer=:referer`), et la page d'arrivée filtrait alors sur ce texte
 * littéral — zéro résultat, sans erreur.
 *
 * @param {object} params
 *   Sous-ensemble de WSOLUTION_PARAMS.
 */
export function wsolutionHrefs(params) {
    const qs = new URLSearchParams(
        Object.entries(params)
            .filter(([, v]) => v != null && String(v) !== '')
            .map(([k, v]) => [k, String(v)]),
    ).toString();
    return { dashboardHref: `/wsolution/dashboard?${qs}`, logsHref: `/wsolution/logs?${qs}` };
}

/** Le webservice et son préfiltre, lus dans une query string. */
export function wsolutionParams(search) {
    const sp = new URLSearchParams(search);
    return Object.fromEntries(WSOLUTION_PARAMS.map((k) => [k, sp.get(k) ?? '']));
}

/**
 * Seuil de précision des agrégations `cardinality` posées par les requêtes
 * WSolution (`precision_threshold`, maximum d'Elasticsearch). En dessous, le
 * décompte est exact ; au-dessus, c'est une estimation, et l'écran le dit.
 */
export const CARDINALITY_PRECISION = 40000;

/**
 * « 2 994 autres adresses » : ce qu'un palmarès borné n'a pas listé, compté en
 * éléments DISTINCTS.
 *
 * Avec des milliers d'IP, les requêtes ne rapatrient que les plus importantes
 * (agrégation `terms` bornée) ; le reste n'est connu que par son volume
 * d'appels (`sum_other_doc_count`). Sans le nombre d'éléments distincts
 * (agrégation `cardinality`), un nœud « Autres » ne dit pas s'il cache deux
 * IP ou vingt mille.
 *
 * @param {number|null|undefined} distinct
 *   Nombre d'éléments distincts vus par le serveur.
 * @param {number} listed
 *   Combien sont affichés nommément.
 * @param {[string, string]} noun
 *   Le nom au singulier et au pluriel : ['adresse', 'adresses'].
 * @return {string|null}
 *   Null quand le serveur n'a pas compté, ou qu'il ne reste rien.
 */
export function othersLabel(distinct, listed, [one, many]) {
    if (distinct == null || !Number.isFinite(Number(distinct))) return null;
    const n = Math.max(0, Number(distinct) - listed);
    if (!n) return null;
    const approx = Number(distinct) > CARDINALITY_PRECISION ? '≈ ' : '';
    return `${approx}${n.toLocaleString('fr-FR')} ${n > 1 ? `autres ${many}` : `autre ${one}`}`;
}
